import type { ComponentProps, ComponentType } from "react";
import type { VariantProps } from "tailwind-variants";
import { tv } from "tailwind-variants";

import type { SvgSlot } from "@/shared/ui/common/svg-slot";
import {
  CheckCircleIcon,
  CloseCircleIcon,
  InfoCircleIcon,
  WarningCircleIcon,
} from "../icons";

const alertVariance = tv({
  slots: {
    root: "rounded-xl border p-4 text-left text-sm font-medium",
    content:
      "flex items-baseline gap-2 [&>svg]:size-[1em] [&>svg]:translate-y-[0.125em]",
  },

  variants: {
    type: {
      info: {
        root: "border-primary-900 bg-primary-50 text-primary-900",
      },
      error: {
        root: "border-red-800 bg-red-100 text-red-800",
      },
      warning: {
        root: "border-yellow-950 bg-yellow-100 text-yellow-950",
      },
      success: {
        root: "border-green-950 bg-green-100 text-green-950",
      },
    },

    variant: {
      solid: { root: "border-transparent" },
      outlined: { root: "bg-transparent" },
    },
  },

  defaultVariants: {
    type: "info",
    variant: "solid",
  },
});

const ALERT_ICON = {
  info: <InfoCircleIcon />,
  error: <CloseCircleIcon />,
  warning: <WarningCircleIcon />,
  success: <CheckCircleIcon />,
} as const;

type AlertProps = ComponentProps<"div"> &
  VariantProps<typeof alertVariance> & {
    withIcon?: boolean;
    icon?: ComponentType<ComponentProps<typeof SvgSlot>>;
  };

export const Alert = ({
  type = "info",
  variant = "solid",
  children,
  icon: Icon,
  withIcon = true,
  className,
}: AlertProps) => {
  const { root, content } = alertVariance({ type, variant });

  const ChildrenContainer = typeof children === "string" ? "p" : "div";

  return (
    <div className={root({ variant, className })}>
      <div className={content()}>
        {withIcon && (Icon ? <Icon /> : ALERT_ICON[type])}

        <ChildrenContainer className="w-full">{children}</ChildrenContainer>
      </div>
    </div>
  );
};
